import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { WaziWordmark, WaziMark } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Star, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ReviewRequest = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || "";
  const [data, setData] = useState(null);
  const [stars, setStars] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [hover, setHover] = useState(0);

  useEffect(() => {
    api.get(`/public/review-request/${jobId}?t=${encodeURIComponent(token)}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [jobId, token]);

  const submit = async () => {
    if (!stars) { toast.error("Please pick a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/public/review-request/${jobId}/submit`, { stars, text, token });
      setDone(true);
    } catch (e) {
      if (e?.response?.status === 409) {
        setDone(true);
        toast.success("You've already reviewed this job — thanks!");
      } else if (e?.response?.status === 403) {
        toast.error("This review link is invalid or expired.");
      } else {
        toast.error("Couldn't submit. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#718096]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  if (data.error || !data.worker) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-[#F7FAFC]">
      <AlertCircle className="w-12 h-12 text-[#DC2626]" />
      <h2 className="font-heading mt-4 text-2xl font-bold text-[#2D3748]">Review link not found</h2>
      <p className="mt-2 text-[#718096]">The link may have expired or the job doesn't exist.</p>
    </div>
  );

  const { worker, customer } = data;

  if (done || data.already_submitted) return (
    <div className="min-h-screen bg-[#F7FAFC] flex flex-col items-center justify-center p-6">
      <div className="bg-white border border-[#25D366]/30 rounded-2xl p-8 max-w-md w-full text-center shadow-sm">
        <div className="w-16 h-16 mx-auto bg-[#25D366]/10 rounded-full flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-[#25D366]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-[#2D3748] mt-4">Thank you!</h2>
        <p className="mt-2 text-[#718096]">Your review helps {worker.business_name} win more work and helps other South Africans find trusted painters.</p>
        <a href={`/u/${worker.slug}`}>
          <Button className="mt-6 rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white" data-testid="view-profile-btn">View {worker.business_name}'s profile</Button>
        </a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7FAFC] py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex justify-center mb-6"><WaziWordmark size={36} /></div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm" data-testid="review-card">
          <div className="flex items-center gap-3">
            {worker.avatar_url ? (
              <img src={worker.avatar_url} alt="" className="w-14 h-14 rounded-2xl object-cover border border-[#E2E8F0]" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-[#25D366]/10 flex items-center justify-center"><WaziMark size={28} /></div>
            )}
            <div>
              <div className="font-heading text-xl font-bold text-[#2D3748]">{worker.business_name}</div>
              <div className="text-sm text-[#718096]">Hi {customer?.name?.split(" ")[0] || "there"} — how was the job?</div>
            </div>
          </div>
          <div className="mt-7">
            <label className="block text-sm font-heading font-medium text-[#2D3748] mb-2">Your rating</label>
            <div className="flex gap-1" data-testid="star-picker">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`star-${s}`}
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setStars(s)}
                  className="p-1 transition-transform hover:scale-110"
                  aria-label={`${s} star${s === 1 ? "" : "s"}`}
                >
                  <Star className={`w-9 h-9 ${s <= (hover || stars) ? "text-[#FFB400] fill-[#FFB400]" : "text-[#E2E8F0]"}`} />
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6">
            <label htmlFor="rt" className="block text-sm font-heading font-medium text-[#2D3748] mb-2">A few words (optional)</label>
            <textarea
              id="rt"
              data-testid="review-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="What stood out about the work?"
              className="w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 outline-none focus:border-[#25D366]"
            />
          </div>
          <Button
            onClick={submit}
            disabled={submitting || !data.valid_token}
            data-testid="submit-review-btn"
            className="mt-6 w-full rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white py-6 font-medium wazi-cta-glow text-base"
          >
            {submitting ? "Submitting…" : "Submit review"}
          </Button>
          {!data.valid_token && (
            <p className="mt-3 text-xs text-[#DC2626] text-center" data-testid="invalid-token-warning">
              This review link is missing a valid token. Please use the link sent on WhatsApp.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewRequest;
